import { Controller } from "@hotwired/stimulus"
import { cropMatrix3d } from "../lib/homography"

export default class extends Controller {
  static targets = ["modal", "image", "label", "prevButton", "nextButton", "telemetry", "loading"]

  connect() {
    // Add keyboard listeners
    this.boundHandleKeyboard = this.handleKeyboard.bind(this)
    document.addEventListener('keydown', this.boundHandleKeyboard)

    // Build the image list for navigation
    this.buildImageList()
    this.currentIndex = -1
    this.zoomed = false
    this.scale = 1

    // Scroll-wheel zoom (#3496), mirroring gallery_controller.js's onWheel --
    // throttled to 50ms and { passive: false } so we can preventDefault and stop
    // the page behind the modal from scrolling while the wheel is over the image.
    // Listens on the image itself (unlike the gallery, this lightbox has no
    // overlay layers sitting above the <img> that a wheel event needs to bubble
    // through, so there's no need to widen the listener to a container).
    this.lastWheelTime = 0
    this.wheelThrottleDelay = 50

    // Drag-to-pan while zoomed (#3496 follow-up). Composed with scale via the
    // SAME transform string (translate(...) scale(...)), matching
    // gallery_controller.js's applyTransform pattern.
    this.panX = 0
    this.panY = 0
    this._panDragging = false
    this._panMoved = false
    // Pixel travel before a press commits to a pan rather than a click. Without
    // this, every pan gesture ends in a click event on mouseup (the browser only
    // suppresses that click after a NATIVE image drag, which we deliberately
    // kill below via preventDragStart) -- and toggleZoom treats any click on a
    // zoomed image as "reset to fit", so an un-thresholded pan would snap back
    // to fit the instant the mouse was released. Mirrors gallery_controller.js's
    // panThreshold, minus its overlay/annotation bail-guard machinery -- this
    // modal has no overlays to bail out for.
    this.panThreshold = 4
    this.boundStartPan = this.startPan.bind(this)
    this.boundOnPan = this.onPan.bind(this)
    this.boundEndPan = this.endPan.bind(this)

    if (this.hasImageTarget) {
      this.imageTarget.addEventListener('wheel', this.onWheel, { passive: false })
      this.imageTarget.addEventListener('mousedown', this.boundStartPan)
      // Kill the native HTML5 image drag-ghost so our own JS pan drives the
      // gesture instead (mirrors gallery_controller.js's preventDragStart).
      this.imageTarget.addEventListener('dragstart', this.preventDragStart)
      window.addEventListener('mousemove', this.boundOnPan)
      window.addEventListener('mouseup', this.boundEndPan)
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeyboard)
    if (this.hasImageTarget) {
      this.imageTarget.removeEventListener('wheel', this.onWheel)
      this.imageTarget.removeEventListener('mousedown', this.boundStartPan)
      this.imageTarget.removeEventListener('dragstart', this.preventDragStart)
    }
    window.removeEventListener('mousemove', this.boundOnPan)
    window.removeEventListener('mouseup', this.boundEndPan)
    clearTimeout(this._wheelSessionTimeout)
  }

  buildImageList() {
    // Find all image elements with preview data WITHIN THIS CONTROLLER'S OWN
    // ELEMENT (not document-wide) -- support both data-field-name (products)
    // and data-image-id (albums). Scoping to this.element (rather than
    // document) is a no-op for every existing consumer -- they already mount
    // the controller on a wrapper containing both its own thumbnails and its
    // own modal -- but it matters for a page with MULTIPLE independent
    // image-preview instances (deck viewer: one controller per slide, so
    // prev/next stays confined to that slide's own photos, never crossing
    // into another slide's).
    const imageElements = this.element.querySelectorAll('[data-image-url][data-field-name], [data-image-url][data-image-id]')

    this.images = Array.from(imageElements).map(el => ({
      url: el.dataset.imageUrl,
      label: el.dataset.label,
      fieldName: el.dataset.fieldName,
      imageId: el.dataset.imageId,
      // Optional crop zoom (image_crop_style helper output). Empty/absent for
      // images with no crop annotation — those render full in the modal. (#1947)
      // For a NON-axis-aligned (perspective) crop this is "" and cropQuad carries
      // the quad instead — the modal straightens it via the homography (ADR-0082).
      cropStyle: el.dataset.cropStyle,
      // Optional perspective crop quad ([[x,y]×4] JSON). Present only when the
      // crop is non-axis-aligned; the lightbox solves a matrix3d to straighten.
      cropQuad: el.dataset.cropQuad,
      // Optional quality-telemetry (#3493) -- original-file dimensions/size from
      // Active Storage blob metadata. Absent for consumers that don't set them
      // (most existing callers), in which case the telemetry line stays hidden.
      width: el.dataset.imageWidth,
      height: el.dataset.imageHeight,
      fileSize: el.dataset.imageFileSize,
      // Use imageId as unique identifier if available, otherwise fieldName
      uniqueId: el.dataset.imageId || el.dataset.fieldName
    })).filter(img => img.url && !img.url.includes('placeholder'))
  }

  show(event) {
    event.preventDefault()
    event.stopPropagation()

    const imageUrl = event.currentTarget.dataset.imageUrl
    const imageId = event.currentTarget.dataset.imageId
    const fieldName = event.currentTarget.dataset.fieldName
    const uniqueId = imageId || fieldName

    if (!imageUrl || imageUrl.includes('placeholder')) {
      return // Don't show modal for placeholder images
    }

    // Find the index of the clicked image using unique identifier
    this.currentIndex = this.images.findIndex(img => img.uniqueId === uniqueId)

    this.showImage(this.currentIndex)
    this.modalTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden' // Prevent background scrolling
    this.updateNavigationButtons()
  }

  showImage(index) {
    if (index < 0 || index >= this.images.length) return

    const image = this.images[index]
    this._currentImage = image
    // Loading indicator must arm BEFORE the src swap below -- it attaches the
    // load/error listeners that clear it, and setting src first could fire load
    // (e.g. an already-cached image) before we're listening for it.
    this.showLoadingIndicator()
    this.imageTarget.src = image.url
    this.labelTarget.textContent = image.label
    this.applyCrop(image)
    this.resetZoom()
    this.updateTelemetry(image)
    this.currentIndex = index
    this.preloadAdjacentImages(index)
  }

  // Loading state (#3496 follow-up). Most consumers pass the full-resolution
  // original as data-image-url -- a multi-MB download that can take seconds, and
  // showImage() swaps imageTarget.src with no other feedback, which reads as the
  // modal being stuck/broken rather than loading (Jay: "takes ages... feels like
  // its broken"). Deliberately NOT downgrading to a smaller variant -- the whole
  // point of this modal (#3493) is inspecting real quality at full resolution.
  //
  // A load token guards against a stale settle(): if the viewer navigates again
  // before the previous image finishes loading, that previous load/error event
  // must not hide the indicator for the image now showing.
  showLoadingIndicator() {
    if (!this.hasLoadingTarget) return
    this.loadingTarget.classList.remove('hidden')

    this._loadToken = (this._loadToken || 0) + 1
    const token = this._loadToken
    const img = this.imageTarget

    const settle = () => {
      img.removeEventListener('load', settle)
      img.removeEventListener('error', settle)
      if (token !== this._loadToken) return // superseded by a later navigation
      this.loadingTarget.classList.add('hidden')
    }
    img.addEventListener('load', settle)
    img.addEventListener('error', settle)
  }

  // Background-preload the immediate neighbours of the now-displayed image
  // (#3496 follow-up) so prev/next feels instant on the second pass once the
  // browser's HTTP cache has them. Not attached to the DOM -- a bare Image()
  // object is enough to trigger and cache the download. Wraps around per the
  // existing prev()/next() navigation. Deliberately just the two neighbours,
  // not the whole list.
  preloadAdjacentImages(index) {
    if (this.images.length <= 1) return

    const prevIndex = index > 0 ? index - 1 : this.images.length - 1
    const nextIndex = index < this.images.length - 1 ? index + 1 : 0

    ;[prevIndex, nextIndex].forEach((i) => {
      const url = this.images[i] && this.images[i].url
      if (!url) return
      const preloadImg = new Image()
      preloadImg.src = url
    })
  }

  // Dimensions/file-size line (#3493). Shown only when the displayed image
  // carries telemetry data attributes -- see buildImageList.
  updateTelemetry(image) {
    if (!this.hasTelemetryTarget) return

    const parts = []
    if (image.width && image.height) parts.push(`${image.width} × ${image.height}`)
    const sizeLabel = this.formatFileSize(image.fileSize)
    if (sizeLabel) parts.push(sizeLabel)

    if (parts.length === 0) {
      this.telemetryTarget.textContent = ''
      this.telemetryTarget.classList.add('hidden')
    } else {
      this.telemetryTarget.textContent = parts.join(' • ')
      this.telemetryTarget.classList.remove('hidden')
    }
  }

  formatFileSize(bytes) {
    const n = parseInt(bytes, 10)
    if (!n || Number.isNaN(n)) return ''
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    return `${(n / (1024 * 1024)).toFixed(2)} MB`
  }

  // True when the currently displayed image carries crop data (ADR-0082) --
  // either a computed perspective matrix3d (cropQuad) or a margin-based
  // pan/zoom (cropStyle). Both already own img.style.transform/cssText (see
  // applyCrop/_applyHomography below), so zoom must never engage for these --
  // composing a scale transform with either isn't safe to guess at. Real
  // consumers of crop data today: client_layout_scores/comparisons/_output,
  // insights/images/_image_card, insights/albums/_image_card.
  _hasActiveCrop() {
    const image = this._currentImage
    if (!image) return false
    return !!image.cropQuad || !!(image.cropStyle && image.cropStyle.trim() !== "")
  }

  // Click-to-zoom (#3493). Toggles between the fit view and a 2.5x zoom
  // centred on the clicked point (transform-origin set from the click
  // coordinates), so clicking a busy area of the photo frames it. Clicking
  // again (anywhere on the now-zoomed image, at ANY scale -- including one
  // reached via scroll-wheel zoom, #3496) resets to fit. Stops propagation so
  // the click never reaches backdropClick and closes the modal instead.
  // No-ops for a cropped image -- see _hasActiveCrop.
  toggleZoom(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this._hasActiveCrop()) return

    // The click that follows a completed pan drag's mouseup -- consume it here
    // rather than resetting zoom out from under the gesture the viewer just made.
    if (this._panMoved) {
      this._panMoved = false
      return
    }

    if (this.zoomed) {
      this.resetZoom()
      return
    }

    const img = this.imageTarget
    const rect = img.getBoundingClientRect()
    const originX = ((event.clientX - rect.left) / rect.width) * 100
    const originY = ((event.clientY - rect.top) / rect.height) * 100

    img.style.transformOrigin = `${originX}% ${originY}%`
    this.scale = 2.5
    this.panX = 0
    this.panY = 0
    this.zoomed = true
    this._applyZoomTransform()
    this._updateCursorForZoomState()
  }

  // Scroll-wheel zoom (#3496). Simpler than gallery_controller.js's onWheel --
  // no pan/rotate stack, just a CSS scale clamped to a lightbox-appropriate
  // 1x-6x (well under the gallery's 20x, which is built for pixel-level MC
  // review, not a quick lightbox glance). The transform-origin is set ONCE at
  // the start of a "wheel session" (from the cursor position at that moment)
  // and held there while the wheel keeps firing, so continuing to scroll zooms
  // further into the same spot rather than re-centring on every tick as the
  // image scales underneath the cursor. A session is considered over once the
  // wheel has been quiet for 400ms, so the next scroll re-reads the cursor.
  // No-ops (never touches img.style.transform) for a cropped image -- see
  // _hasActiveCrop -- though the event is still preventDefault()'d either way
  // so the page behind the modal never scrolls.
  onWheel = (event) => {
    const now = Date.now()
    if (now - this.lastWheelTime < this.wheelThrottleDelay) {
      event.preventDefault()
      return
    }
    this.lastWheelTime = now
    event.preventDefault()

    if (this._hasActiveCrop()) return

    const img = this.imageTarget

    clearTimeout(this._wheelSessionTimeout)
    if (!this._wheelSessionActive) {
      const rect = img.getBoundingClientRect()
      const originX = ((event.clientX - rect.left) / rect.width) * 100
      const originY = ((event.clientY - rect.top) / rect.height) * 100
      img.style.transformOrigin = `${originX}% ${originY}%`
      this._wheelSessionActive = true
    }
    this._wheelSessionTimeout = setTimeout(() => { this._wheelSessionActive = false }, 400)

    const newScale = Math.max(1, Math.min((this.scale || 1) * (event.deltaY < 0 ? 1.1 : 0.9), 6))
    this.scale = newScale
    this.zoomed = newScale > 1

    if (this.zoomed) {
      const clamped = this._clampPan(this.panX, this.panY)
      this.panX = clamped.x
      this.panY = clamped.y
    } else {
      // Back to fit -- don't let a stale pan offset carry into the next zoom.
      this.panX = 0
      this.panY = 0
    }

    this._applyZoomTransform()
    this._updateCursorForZoomState()
  }

  // Compose the current pan + scale into the SAME transform (mirroring
  // gallery_controller.js's applyTransform), the single place both toggleZoom
  // and onWheel write img.style.transform from.
  _applyZoomTransform() {
    const img = this.imageTarget
    img.style.transform = this.zoomed ? `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})` : ''
  }

  // Cursor for the current zoom/drag state (#3496 follow-up). Zoomed shows
  // grab/grabbing (drag affordance) instead of the zoom-out magnifier -- Jay:
  // "the cursor magnify +/- means I can't grab to drag." Unzoomed keeps the
  // existing zoom-in magnifier. No-ops under an active crop -- resetZoom's crop
  // branch owns cursor classes there (zoom/pan never apply).
  _updateCursorForZoomState() {
    if (this._hasActiveCrop()) return
    const img = this.imageTarget
    if (this.zoomed) {
      img.classList.remove('cursor-zoom-in', 'cursor-zoom-out', 'cursor-grabbing')
      img.classList.add('cursor-grab')
    } else {
      img.classList.remove('cursor-grab', 'cursor-grabbing', 'cursor-zoom-out')
      img.classList.add('cursor-zoom-in')
    }
  }

  resetZoom() {
    if (!this.hasImageTarget) return

    const img = this.imageTarget
    this.zoomed = false
    this.scale = 1
    this.panX = 0
    this.panY = 0
    this._panDragging = false
    this._panMoved = false
    this._wheelSessionActive = false
    clearTimeout(this._wheelSessionTimeout)

    if (this._hasActiveCrop()) {
      // applyCrop() (called just before this, in showImage) already owns
      // style.transform/cssText for this image -- a computed matrix3d for a
      // perspective cropQuad, or a margin-based cropStyle. Zoom (and therefore
      // pan) never engages here (see toggleZoom/onWheel/startPan), so there is
      // nothing of ours to undo, and clearing style.transform unconditionally
      // would clobber the crop itself. Just drop the zoom/pan cursor classes
      // since neither is available.
      img.classList.remove('cursor-zoom-in', 'cursor-zoom-out', 'cursor-grab', 'cursor-grabbing')
      return
    }

    img.style.transform = ''
    img.style.transformOrigin = 'center center'
    img.classList.remove('cursor-zoom-out', 'cursor-grab', 'cursor-grabbing')
    img.classList.add('cursor-zoom-in')
  }

  // --- Drag-to-pan while zoomed (#3496 follow-up) ----------------------------

  // Kill the browser's native HTML5 image drag (belt-and-braces alongside
  // draggable="false" on the <img> in the modal partial) so our own mousedown/
  // mousemove/mouseup pan drives the gesture instead of a native ghost-drag.
  preventDragStart = (event) => event.preventDefault()

  // A left press on the image ARMS a potential pan -- only when already zoomed
  // and not on a cropped image (crop owns img.style.transform; see
  // _hasActiveCrop). Mirrors gallery_controller.js's startDrag, minus its
  // overlay bail-guard (no annotation layers here). Does not commit to a pan
  // (no cursor change, no preventDefault) until the pointer clears panThreshold
  // in onPan -- a no-move press still falls through to toggleZoom as a click.
  startPan = (event) => {
    if (event.button !== 0) return
    if (!this.zoomed || this._hasActiveCrop()) return

    this._panDragging = true
    this._panMoved = false
    this._panStartClientX = event.clientX
    this._panStartClientY = event.clientY
    this._panOriginX = this.panX
    this._panOriginY = this.panY
    // Measure the pan bounds ONCE for this drag gesture (#3496 lag fix) --
    // onPan's hot per-tick loop then clamps with pure arithmetic against this
    // cached value instead of forcing a layout reflow on every mousemove. Scale
    // cannot change mid-drag (only mid-zoom, which isn't concurrent with a pan
    // -- see the this.zoomed gate above), so caching at drag-start is safe.
    this._panBounds = this._measurePanBounds()
  }

  onPan = (event) => {
    if (!this._panDragging) return

    const dx = event.clientX - this._panStartClientX
    const dy = event.clientY - this._panStartClientY

    if (!this._panMoved) {
      if (Math.hypot(dx, dy) < this.panThreshold) return
      // Threshold crossed -- commit to a pan. Suspend the CSS transition
      // (#3496 lag fix, Jay: "feels a bit laggy vs the response modal image")
      // for the duration of the drag: transition-transform is correct and
      // wanted for the discrete jumps of click-to-zoom/wheel-zoom (a nice
      // eased animation), but wrong for a continuous 1:1 drag -- with it
      // active, every mousemove's style.transform write eases toward its new
      // value over 150ms instead of snapping instantly, so the image visibly
      // trails the cursor. Restored in endPan. Setting the inline style wins
      // over the Tailwind transition-transform class on the <img> (inline
      // always beats a non-!important class) without touching that class list.
      this._panMoved = true
      this.imageTarget.style.transition = 'none'
      this.imageTarget.classList.remove('cursor-grab')
      this.imageTarget.classList.add('cursor-grabbing')
    }

    event.preventDefault()
    const clamped = this._clampPan(this._panOriginX + dx, this._panOriginY + dy, this._panBounds)
    this.panX = clamped.x
    this.panY = clamped.y
    this._applyZoomTransform()
  }

  endPan = () => {
    if (!this._panDragging) return
    this._panDragging = false
    // Restore the transition (see onPan) so click-to-zoom/wheel-zoom keep
    // their smooth eased animation -- only the active drag itself was instant.
    this.imageTarget.style.transition = ''
    if (this.zoomed && !this._hasActiveCrop()) {
      this.imageTarget.classList.remove('cursor-grabbing')
      this.imageTarget.classList.add('cursor-grab')
    }
    // _panMoved is deliberately left as-is here -- the click event that follows
    // this mouseup (when a real drag happened) is read and cleared once by
    // toggleZoom, so it can ignore that trailing click instead of resetting zoom.
  }

  // Measure the current pan bounds from the DOM (#3496 lag fix). Split out of
  // _clampPan so a hot per-tick caller (onPan) can measure ONCE at drag-start
  // and clamp afterwards with pure arithmetic -- see startPan/onPan. Not
  // pixel-perfect (the transform-origin from a click/wheel zoom may not be
  // centred), just a "keep at least part of the image visible" bound: allow
  // pan up to half the difference between the scaled image size and its
  // container, on each axis. offsetWidth/Height are the image's LAYOUT box,
  // unaffected by the CSS transform, so multiplying by scale gives its
  // current visual size.
  _measurePanBounds() {
    const img = this.imageTarget
    const container = img.parentElement
    if (!container) return { maxX: 0, maxY: 0 }

    const scale = this.scale || 1
    const scaledWidth = img.offsetWidth * scale
    const scaledHeight = img.offsetHeight * scale

    return {
      maxX: Math.max(0, (scaledWidth - container.offsetWidth) / 2),
      maxY: Math.max(0, (scaledHeight - container.offsetHeight) / 2)
    }
  }

  // Clamp a candidate pan offset so the image can't be dragged fully off-frame.
  // Pass pre-measured `bounds` (from _measurePanBounds, cached once at
  // drag-start -- see startPan) to avoid a live DOM read; onPan's hot per-tick
  // loop always does this. onWheel's zoom-driven pan-clamp isn't a hot loop, so
  // it omits `bounds` and this measures live -- values are identical either way.
  _clampPan(x, y, bounds) {
    const { maxX, maxY } = bounds || this._measurePanBounds()

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y))
    }
  }

  // Apply (or clear) the crop on the modal <img>. Two paths (ADR-0082):
  //   1. Axis-aligned crop — the image_crop_style margin-zoom string (cropStyle):
  //      blow the image up and pan it inside an overflow-hidden wrapper. (#1947)
  //   2. Perspective crop — a non-axis-aligned quad (cropQuad): solve a projective
  //      homography (matrix3d) that straightens + frames into the wrapper, with
  //      transform-origin 0 0. Pixel-dependent, so re-solved on each image load.
  // With neither, the default object-contain full-image view is restored.
  applyCrop(image) {
    const img = this.imageTarget
    img.style.cssText = ""

    if (image.cropQuad) {
      img.classList.remove("object-contain")
      if (img.parentElement) img.parentElement.classList.add("overflow-hidden")
      // The matrix3d needs the rendered frame + natural size, so solve after load.
      if (img.complete) {
        this._applyHomography(image.cropQuad)
      } else {
        this._pendingQuad = image.cropQuad
        if (!this._boundOnLoad) {
          this._boundOnLoad = () => {
            if (this._pendingQuad) this._applyHomography(this._pendingQuad)
          }
          img.addEventListener("load", this._boundOnLoad)
        }
      }
    } else if (image.cropStyle && image.cropStyle.trim() !== "") {
      img.classList.remove("object-contain")
      img.style.cssText = image.cropStyle
      if (img.parentElement) img.parentElement.classList.add("overflow-hidden")
    } else {
      img.classList.add("object-contain")
    }
  }

  _applyHomography(quadJson) {
    const img = this.imageTarget
    const frame = img.parentElement
    if (!frame) return

    let quad
    try {
      quad = JSON.parse(quadJson)
    } catch (e) {
      return
    }

    const matrix = cropMatrix3d(frame, img, quad)
    if (!matrix) return
    img.style.transformOrigin = "0 0"
    img.style.transform = matrix
  }

  prev(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Wraparound: go to last image if at first
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1
    this.showImage(newIndex)
    this.updateNavigationButtons()
  }

  next(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Wraparound: go to first image if at last
    const newIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0
    this.showImage(newIndex)
    this.updateNavigationButtons()
  }

  updateNavigationButtons() {
    // Always show both buttons with wraparound navigation
    // Only hide if there's only one image
    if (this.images.length <= 1) {
      this.prevButtonTarget.classList.add('hidden')
      this.nextButtonTarget.classList.add('hidden')
    } else {
      this.prevButtonTarget.classList.remove('hidden')
      this.nextButtonTarget.classList.remove('hidden')
    }
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.modalTarget.classList.add('hidden')
    document.body.style.overflow = '' // Restore scrolling
    this.resetZoom()
  }

  handleKeyboard(event) {
    if (!this.hasModalTarget || this.modalTarget.classList.contains('hidden')) return

    switch(event.key) {
      case 'Escape':
        this.close()
        break
      case 'ArrowLeft':
        event.preventDefault()
        this.prev()
        break
      case 'ArrowRight':
        event.preventDefault()
        this.next()
        break
    }
  }

  // Close when clicking the backdrop (not the image itself)
  backdropClick(event) {
    if (event.target === event.currentTarget) {
      this.close(event)
    }
  }
}
